import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
	ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import {
	slideXParentMenu,
	slideXSubMenu,
} from '@jepto/core/animations/core.animations';
import { ClientsInsightService } from '@jepto/features/portfolio/services/clients-insight.service';
import { JepGlobalDataService } from '@jepto/shared/services/jep-global-data/jep-global-data.service';

import * as insightModel from '@jepto/features/insights/store/insights/models/insights.model';
import * as fromPortfolioModuleRootStoreAsFeature from '@jepto/features/portfolio/store/index';
import * as fromSharedAccountStore from '@jepto/shared/store/account/index';
import { subscriptionPlanNameFromAccount } from '@jepto/shared/store/account/models/account.model';
import * as fromSharedStore from '@jepto/shared/store/index';
import { Store } from '@ngrx/store';

import { sortKeySplitter } from '@jepto/shared/utils/collection';

import { BehaviorSubject, Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { error, log } from '@jepto/shared/utils/logger';
import {
	markForCheckOnEmit,
	safeUpdateInSubscription,
} from '@jepto/shared/utils/change-detection';


import { mainSidebarCurrentWidthValue } from './main-sidebar-current-width.util';
import {
	NAV_OVERFLOW_FADE_ENABLED,
	navOverflowFadeState,
} from './nav-overflow-fade.util';
@Component({
	selector: 'jep-dash-menu',
	templateUrl: './menubar.component.html',
	styleUrls: ['./menubar.component.scss'],
	animations: [slideXParentMenu, slideXSubMenu],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class DashMenuComponent implements OnInit, AfterViewInit, OnDestroy {
	unsubscribe$ = new Subject<void>();
	activePortfolioRouterLink: boolean;
	activeClientRouterLink: boolean;
	activeAdminRouterLink: boolean;
	activeKPIManagementRouterLink: boolean;
	activeClientSettingsRouterLink: boolean;
	showClientSettings: boolean;
	selectedClientId: string = null;
	activeBudgetTrackerRouterLink: boolean;
	activeAnomalyDetectionRouterLink: boolean;
	activeAutomationRouterLink: boolean;
	activeInsightsRouterLink: boolean;
	activeDataWareHouseRouterLink: boolean;
	activeExtensionLink: boolean;
	activeAnalysisLink: boolean;
	activeLocalListingsLink: boolean;
	activeAiVisibilityLink: boolean;
	activeReviewsLink: boolean;
	activeSocialPostsLinks: boolean;
	activeGetStartedLink: boolean;
	currentUrlClientId: string;
	activeDataSourcesRouterLink: boolean;
	activeDataChatRouterLink: boolean;
	activeReportsRouterLink: boolean;
	showAiVisibilityMenu: boolean = true;
	accountType: string;
	insightsUnresolvedCount$: BehaviorSubject<insightModel.insightsCount> =
		new BehaviorSubject(null);
	extensionExpanded: boolean = false;
	isCollapse: boolean = false;

	showGetStartedTabFlag: boolean = false;
	navMoreAbove = false;
	navMoreBelow = false;

	@ViewChild('navMenu') private navMenu?: ElementRef<HTMLElement>;
	private navOverflowFadeObserver?: ResizeObserver;

	// Add this property to track the previous client ID
	private previousClientId: string | null = null;

	constructor(
		private router: Router,
		private portofolioRootStore: Store<fromPortfolioModuleRootStoreAsFeature.PortfolioModuleReducers>,
		private sharedStore: Store<fromSharedStore.SharedModuleReducers>,
		private clientsInsightService: ClientsInsightService,
		private jepGlobalDataService: JepGlobalDataService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit() {
		// Initialize local flag from service flag if it's already set
		// This ensures the menu persists across navigations if the service flag was set previously
		if (this.jepGlobalDataService.showGetStartedTabFlag !== undefined) {
			this.showGetStartedTabFlag =
				this.jepGlobalDataService.showGetStartedTabFlag;
		}
		this.autoRun();
	}

	ngAfterViewInit(): void {
		this.bindNavOverflowFade();
		this.jepGlobalDataService.isCollapse
			.pipe(take(1))
			.subscribe(collapsed => {
				safeUpdateInSubscription(this.cdr, () => {
					this.isCollapse = collapsed;
					this.applySidebarWidths(collapsed);
				});
			});
	}

	ngOnDestroy() {
		this.navOverflowFadeObserver?.disconnect();
		this.unsubscribe$.next();
		this.unsubscribe$.complete();

		// Reset client tracking
		this.previousClientId = null;
		this.selectedClientId = null;
	}

	autoRun() {
		this.listenOnDeleteSingleClient();
		this.getAccountData();
		// Call immediately so onboarding tab appears as soon as API returns, not after account load
		this.showGetStartedTab();
		this.setClientIdForBusinessAccount();
		this.initMenuBar();
		this.listenOnGetSelectedClient();
		this.getSelectedClientAccountOnRouter();
		this.listenToRouterEvents();
		this.initCurrentUrlClientId();
		this.initOnRefreshProcessClientSettingsRoutes();
		this.listeners();

		// Trigger initial insights query if we have a client ID
		this.triggerInitialInsightsQuery();
	}

	/**
	 * Trigger initial insights query only if we have a client ID
	 */
	private triggerInitialInsightsQuery(): void {
		if (this.currentUrlClientId) {
			log(
				`[DashMenuComponent] Initial load with client ID: ${this.currentUrlClientId}, triggering client insights query`
			);
			this.clientsInsightService.getInsightsCount(
				false,
				this.currentUrlClientId
			);
		} else {
			log(
				'[DashMenuComponent] Initial load with no client ID, triggering global insights query'
			);
			this.clientsInsightService.getInsightsCount(false);
		}
	}

	listeners() {
		this.listenOnGetInsightsUnderAccountActive();
		this.listenOnGetTaskItem();
		this.listenForRouterEvents();
	}

	listenOnGetTaskItem(): void {
		this.jepGlobalDataService.onGetTaskItem
			.pipe(markForCheckOnEmit(this.cdr), takeUntil(this.unsubscribe$))
			.subscribe(result => {
				log('clg listenOnGetTaskItem', result);
				if (result.isOnboardingCompleted) {
					this.jepGlobalDataService.showGetStartedTabFlag =
						result.isOnboardingCompleted;
					safeUpdateInSubscription(this.cdr, () => {
						this.showGetStartedTabFlag =
							result.isOnboardingCompleted;
					});
				}
			});
	}

	showGetStartedTab(): void {
		this.jepGlobalDataService
			.showOnboardingTab()
			.pipe(markForCheckOnEmit(this.cdr), takeUntil(this.unsubscribe$))
			.subscribe(result => {
				if (result.state) {
					safeUpdateInSubscription(this.cdr, () => {
						this.showGetStartedTabFlag =
							result.data['showOnboarding'];
					});
					this.jepGlobalDataService.showGetStartedTabFlag =
						this.showGetStartedTabFlag;
					if (
						result['data']['showOnboarding'] &&
						result.data['onboarding']['channels'].length === 0 &&
						result.data['onboarding']['objectives'].length === 0
					) {
						this.jepGlobalDataService.routeFlagGetstarted = true;
					} else {
						this.jepGlobalDataService.routeFlagGetstarted = false;
					}
					log(
						'showOnboardingTab',
						result,
						this.router.url,
						localStorage.getItem('onb')
					);
					// if channel and obj are both empty show modal survey
					this.navigateToOnboarding();
				} else {
					safeUpdateInSubscription(this.cdr, () => {
						this.showGetStartedTabFlag = false;
					});
					this.jepGlobalDataService.showGetStartedTabFlag =
						this.showGetStartedTabFlag;
				}
			});
	}

	navigateToOnboarding(): void {
		const flag: string = localStorage.getItem('onb');
		if (this.jepGlobalDataService.showGetStartedTabFlag && Boolean(flag)) {
			localStorage.removeItem('onb');
			setTimeout(() => {
				this.router.navigate(['get-started']);
			}, 800);
		}
	}

	listenForRouterEvents() {
		// Handle initial navigation (for page reload)
		this.handleRouterNavigation(this.router.url);

		// Subscribe to future navigation events
		this.router.events
			.pipe(
				filter(event => event instanceof NavigationEnd),
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$)
			)
			.subscribe(event => {
				if (event instanceof NavigationEnd) {
					this.handleRouterNavigation(event.urlAfterRedirects);
				}
			});
	}

	private handleRouterNavigation(url: string): void {
		safeUpdateInSubscription(this.cdr, () => {
			// Only sync if service flag is explicitly true (from API response)
			// Don't overwrite local true with service false/undefined during async gap
			// This preserves menu visibility once it's been set to true
			if (this.jepGlobalDataService.showGetStartedTabFlag === true) {
				this.showGetStartedTabFlag = true;
			}
			// If local is true and service is false/undefined, keep local true
			// If local is false and service is false/undefined, keep local false
		});

		// Remove query parameters and fragments before parsing
		const cleanUrl = url.split('?')[0].split('#')[0];

		// Extract selectedClientId from URL
		if (cleanUrl.includes('/clients/')) {
			const urlParts = cleanUrl.split('/');
			if (urlParts.length >= 3 && urlParts[1] === 'clients') {
				this.selectedClientId = urlParts[2];
			}
		} else {
			// Clear selectedClientId if not on a client route
			this.selectedClientId = null;
		}
	}

	listenOnDeleteSingleClient(): void {
		this.jepGlobalDataService.onDeleteSingleClient
			.pipe(markForCheckOnEmit(this.cdr), takeUntil(this.unsubscribe$))
			.subscribe((result: boolean) => {
				if (result === true) {
					this.selectedClientId = null;
				}
			});
	}

	initCurrentUrlClientId() {
		this.currentUrlClientId = sortKeySplitter(
			window.location.pathname,
			'/',
			2
		);
		// Initialize the previous client ID
		this.previousClientId = this.currentUrlClientId;
	}

	/**
	 * Manually trigger insights query for a specific client
	 * Call this method when you specifically want to fetch insights for a client
	 */
	public triggerClientInsightsQuery(clientId: string): void {
		if (clientId) {
			log(
				`[DashMenuComponent] Manually triggering insights query for client: ${clientId}`
			);
			this.clientsInsightService.getInsightsCount(false, clientId);
		}
	}

	/**
	 * Manually trigger global insights query
	 * Call this method when you specifically want to fetch global insights
	 */
	public triggerGlobalInsightsQuery(): void {
		log('[DashMenuComponent] Manually triggering global insights query');
		this.clientsInsightService.getInsightsCount(false);
	}

	getAccountData() {
		this.sharedStore
			.select(fromSharedAccountStore.getAccountData)
			.pipe(
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$),
				filter(
					(result): result is fromSharedAccountStore.IAccount =>
						result != null
				)
			)
			.subscribe((result: fromSharedAccountStore.IAccount) => {
				this.accountType = subscriptionPlanNameFromAccount(result);
				this.setClientIdForBusinessAccount();
				safeUpdateInSubscription(this.cdr, () => {});
			});
	}

	listenOnGetSelectedClient(): boolean {
		this.portofolioRootStore
			.select(fromPortfolioModuleRootStoreAsFeature.selectedClient)
			.pipe(
				tap(res => {
					log('menubar[246]', res);
				}),
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$),
				filter(
					result => result && result !== undefined && Boolean(result)
				)
			)
			.subscribe(
				(
					result: fromPortfolioModuleRootStoreAsFeature.IClientDetails
				) => {
					if (result) {
						const newClientId = result.sortKey
							? sortKeySplitter(result.sortKey, '#', 2)
							: result.id;

						// Only update if newClientId is valid (not undefined)
						if (newClientId) {
							// Check if the selected client has changed
							if (this.selectedClientId !== newClientId) {
								const previousClientId = this.selectedClientId;
								this.selectedClientId = newClientId;

								// Enable client settings when a client is selected
								this.showClientSettings = true;

								// Trigger insights query if client changed
								if (
									previousClientId &&
									previousClientId !== newClientId
								) {
									log(
										`[DashMenuComponent] Selected client changed from ${previousClientId} to ${newClientId}, triggering insights query`
									);
									this.clientsInsightService.getInsightsCount(
										false,
										newClientId
									);
								}
							}
						}
					} else {
						// Handle case when no client is selected (global view)
						if (this.selectedClientId) {
							log(
								`[DashMenuComponent] No client selected, transitioning to global view, triggering global insights query`
							);
							this.selectedClientId = null;
							this.showClientSettings = false;
							this.clientsInsightService.getInsightsCount(false);
						}
					}
				}
			);
		return true;
	}

	routeAfterClientCreation(clientId: string, route: string) {
		log('clg routeAfterClientCreation', clientId, route);

		setTimeout(() => {
			this.router.navigate(['clients', clientId, route]);
		}, 0);
	}

	listenToRouterEvents() {
		this.router.events
			.pipe(
				filter(event => event instanceof NavigationEnd),
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$)
			)
			.subscribe(event => {
				if (event instanceof NavigationEnd) {
					const currentUrl: string = event.urlAfterRedirects;
					if (
						currentUrl.includes('/clients') &&
						currentUrl.includes('/kpis') &&
						currentUrl.split('/').length === 4
					) {
						// this.showClientSettings = true;
					}
				}
			});
	}

	listenOnGetInsightsUnderAccountActive() {
		this.insightsUnresolvedCount$ =
			this.jepGlobalDataService.onGetInsightCount;
	}

	getSelectedClientAccountOnRouter() {
		let routerURL = this.router.url;
		if (routerURL.includes('/clients') && routerURL.includes('/settings')) {
			this.selectedClientId = routerURL.split('/')[2];
			this.showClientSettings = true;
		} else if (
			routerURL.includes('/clients') &&
			routerURL.split('/').length >= 3
		) {
			// If we're on any client route, enable client settings
			this.selectedClientId = routerURL.split('/')[2];
			this.showClientSettings = true;
		}
	}

	setClientIdForBusinessAccount() {
		if (
			this.accountType === 'Business' ||
			this.accountType === 'Data Warehouse' ||
			this.accountType === 'Basic' ||
			this.accountType === 'Reporting'
		) {
			this.selectedClientId = localStorage.getItem('businessClientId');
		}
	}

	initMenuBar() {
		this.handleSelectedMenu(this.router.url);
		this.router.events
			.pipe(
				filter(event => event instanceof NavigationEnd),
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$)
			)
			.subscribe((event: NavigationEnd) => {
				this.setClientIdForBusinessAccount();
				this.handleSelectedMenu(event.url);

				// Check for client changes and trigger insights only when needed
				this.handleClientChangeForInsights(event.url);
			});
	}

	/**
	 * Handle client changes and trigger insights queries only when necessary
	 */
	private handleClientChangeForInsights(currentUrl: string): void {
		const currentClientId = sortKeySplitter(currentUrl, '/', 2);

		// Check if we're going from a selected client view to global view (no selected client)
		if (this.previousClientId && !currentClientId) {
			this.handleGlobalViewTransition();
		}

		// Check if we're changing from one client to another
		if (
			this.previousClientId &&
			currentClientId &&
			this.previousClientId !== currentClientId
		) {
			this.handleClientChange(this.previousClientId, currentClientId);
		}

		// Update the previous client ID
		this.previousClientId = currentClientId;
	}

	/**
	 * Handle transition from selected client view to global view
	 */
	private handleGlobalViewTransition(): void {
		log(
			'[DashMenuComponent] Transitioned to global view, triggering global insights query'
		);
		// Trigger global insights query (no clientId)
		this.clientsInsightService.getInsightsCount(false);
	}

	/**
	 * Handle when changing from one client to another
	 */
	private handleClientChange(
		previousClientId: string,
		newClientId: string
	): void {
		log(
			`[DashMenuComponent] Client changed from ${previousClientId} to ${newClientId}, triggering insights query`
		);
		// Trigger insights query for the new client
		this.clientsInsightService.getInsightsCount(false, newClientId);
	}

	handleSelectedMenu(currentUrl: string) {
		const urlCollections: Array<string> = currentUrl.split('/');
		const destination: string = urlCollections.pop();
		if (currentUrl.includes('/kpis')) {
			this.processKPIRoutes();
		} else if (currentUrl.includes('/budgets')) {
			this.processBudgetsRoutes();
		} else if (currentUrl.includes('/ai-visibility')) {
			// Checked ahead of the generic `/settings` branch below: AI
			// Visibility's own Settings tab lives at `/ai-visibility/settings`
			// (global and client-scoped), which otherwise matches that branch
			// first and lights up the app's Settings menu item instead.
			this.processAiVisibilityRoutes();
		} else if (currentUrl.includes('/settings')) {
			this.processClientSettingsRoutes('settings');
		} else if (currentUrl.includes('/clients')) {
			if (
				currentUrl.includes('settings') ||
				currentUrl.includes('kpis')
			) {
				this.processClientSettingsRoutes(destination);
			} else if (currentUrl.includes('anomalies')) {
				this.processAnomalyRoutes();
			} else if (currentUrl.includes('automations')) {
				this.processAutomationRoutes();
			} else if (currentUrl.includes('insights')) {
				this.processInsightsRoutes();
			} else if (currentUrl.includes('/local')) {
				this.processLocalListingsRoutes();
			} else if (currentUrl.includes('/reviews')) {
				this.processReviewsRoutes();
			} else if (currentUrl.includes('/ai-visibility')) {
				this.processAiVisibilityRoutes();
			} else if (currentUrl.includes('/data-sources')) {
				this.processDataSourcesRoutes();
			} else if (currentUrl.includes('/chats')) {
				this.processDataChatRoutes();
			} else if (currentUrl.includes('/reports')) {
				this.processReportsRoutes();
			} else if (currentUrl.includes('/posts')) {
				this.processSocialLinkRoutes();
			} else {
				this.processPortfolioRoutes(destination);
			}
		} else if (currentUrl.includes('/admin')) {
			this.processAdminRoutes(destination);
		} else if (currentUrl.includes('/automations')) {
			this.processAutomationRoutes();
		} else if (currentUrl.includes('anomalies')) {
			this.processAnomalyRoutes();
		} else if (currentUrl.includes('/insights')) {
			this.processInsightsRoutes();
		} else if (currentUrl.includes('/pipelines')) {
			this.processDataWareHouseRoutes();
		} else if (currentUrl.includes('/extension')) {
			this.processExtensionRoutes();
		} else if (currentUrl.includes('/analysis')) {
			this.processAnalysisRoutes();
		} else if (currentUrl.includes('/local')) {
			this.processLocalListingsRoutes();
		} else if (currentUrl.includes('/reviews')) {
			this.processReviewsRoutes();
		} else if (currentUrl.includes('/ai-visibility')) {
			this.processAiVisibilityRoutes();
		} else if (currentUrl.includes('/posts')) {
			this.processSocialLinkRoutes();
		} else if (currentUrl.includes('/get-started')) {
			this.processGetStartedLinkRoutes();
		} else if (currentUrl.includes('/data-sources')) {
			this.processDataSourcesRoutes();
		} else if (currentUrl.includes('/chats')) {
			this.processDataChatRoutes();
		} else if (currentUrl.includes('/reports')) {
			this.processReportsRoutes();
		}
	}

	processKPIRoutes() {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeAnalysisLink = false;
		this.activeLocalListingsLink = false;
		this.activeSocialPostsLinks = false;
		this.activeGetStartedLink = false;
		this.activeReviewsLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
		this.activeKPIManagementRouterLink = true;
	}

	processPortfolioRoutes(destination: string) {
		this.activeAdminRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.showClientSettings = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeAnalysisLink = false;
		this.activeLocalListingsLink = false;
		this.activeReviewsLink = false;
		this.activeSocialPostsLinks = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
		if (destination === 'archived' || destination === 'clients') {
			this.activePortfolioRouterLink = true;
		}
	}

	processAutomationRoutes() {
		this.activeAdminRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activePortfolioRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeLocalListingsLink = false;
		this.activeAutomationRouterLink = true;
		this.activeAnalysisLink = false;
		this.activeReviewsLink = false;
		this.activeSocialPostsLinks = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
	}

	processAdminRoutes(destination: string) {
		this.activeReviewsLink = false;
		this.activePortfolioRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeAnalysisLink = false;
		this.activeLocalListingsLink = false;
		this.activeSocialPostsLinks = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;

		if (
			destination === 'settings' ||
			destination === 'connections' ||
			destination === 'integrations' ||
			destination === 'users' ||
			destination === 'subscription' ||
			destination === 'admin'
		) {
			this.activeAdminRouterLink = true;
		}
	}

	processClientSettingsRoutes(destination: string) {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeLocalListingsLink = false;
		this.activeAnalysisLink = false;
		this.activeSocialPostsLinks = false;
		this.activeReviewsLink = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
		if (destination === 'settings') {
			this.activeClientSettingsRouterLink = true;
			this.showClientSettings = true;
		}
	}

	initOnRefreshProcessClientSettingsRoutes() {
		// AI Visibility's own Settings tab (`/ai-visibility/settings`) also
		// matches `/settings` — exclude it so a refresh there doesn't
		// re-activate the app's Settings menu item over `handleSelectedMenu`'s
		// correct result from `initMenuBar()`, which runs first.
		this.activeClientSettingsRouterLink =
			this.router.url.includes('/settings') &&
			!this.router.url.includes('/ai-visibility');
	}

	processBudgetsRoutes() {
		// Extract clientId from budgets route if present
		const currentUrl = this.router.url;
		if (
			currentUrl.includes('/clients/') &&
			currentUrl.includes('/budgets')
		) {
			const urlParts = currentUrl.split('/');
			if (urlParts.length >= 3 && urlParts[1] === 'clients') {
				this.selectedClientId = urlParts[2];
			}
		}

		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeLocalListingsLink = false;
		this.activeBudgetTrackerRouterLink = true;
		this.activeAnalysisLink = false;
		this.activeSocialPostsLinks = false;
		this.activeReviewsLink = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
	}

	processAnomalyRoutes() {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeLocalListingsLink = false;
		this.activeAnomalyDetectionRouterLink = true;
		this.activeAnalysisLink = false;
		this.activeSocialPostsLinks = false;
		this.activeReviewsLink = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
	}

	processInsightsRoutes() {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeAnalysisLink = false;
		this.activeLocalListingsLink = false;
		this.activeReviewsLink = false;
		this.activeSocialPostsLinks = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeInsightsRouterLink = true;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
	}

	processBudgetsCreateRoutes() {
		if (
			window.location.pathname ===
			`/clients/${this.currentUrlClientId}/budgets/create`
		) {
			this.activeBudgetTrackerRouterLink = true;
		}
	}

	processDataWareHouseRoutes() {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.showClientSettings = false;
		this.activeExtensionLink = false;
		this.activeReviewsLink = false;
		this.activeAnalysisLink = false;
		this.activeLocalListingsLink = false;
		this.activeSocialPostsLinks = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeDataWareHouseRouterLink = true;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
	}

	processExtensionRoutes() {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.showClientSettings = true;
		this.activeExtensionLink = true;
		this.activeDataWareHouseRouterLink = false;
		this.activeLocalListingsLink = false;
		this.activeAnalysisLink = false;
		this.activeSocialPostsLinks = false;
		this.activeGetStartedLink = false;
		this.activeReviewsLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
	}

	processAnalysisRoutes() {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.showClientSettings = true;
		this.activeExtensionLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeLocalListingsLink = false;
		this.activeReviewsLink = false;
		this.activeSocialPostsLinks = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeAnalysisLink = true;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
	}

	processLocalListingsRoutes() {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeAnalysisLink = false;
		this.activeReviewsLink = false;
		this.activeSocialPostsLinks = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeLocalListingsLink = true;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
	}

	processAiVisibilityRoutes() {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeAnalysisLink = false;
		this.activeReviewsLink = false;
		this.activeSocialPostsLinks = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeLocalListingsLink = false;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = true;
	}

	processReviewsRoutes() {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeAnalysisLink = false;
		this.activeLocalListingsLink = false;
		this.activeSocialPostsLinks = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeReviewsLink = true;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
	}

	processSocialLinkRoutes() {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeAnalysisLink = false;
		this.activeLocalListingsLink = false;
		this.activeReviewsLink = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeSocialPostsLinks = true;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
	}

	processGetStartedLinkRoutes() {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeAnalysisLink = false;
		this.activeLocalListingsLink = false;
		this.activeReviewsLink = false;
		this.activeSocialPostsLinks = false;
		this.activeDataSourcesRouterLink = false;
		this.activeGetStartedLink = true;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
	}

	processDataSourcesRoutes() {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeAnalysisLink = false;
		this.activeLocalListingsLink = false;
		this.activeReviewsLink = false;
		this.activeSocialPostsLinks = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = true;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
	}

	processDataChatRoutes() {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeAnalysisLink = false;
		this.activeLocalListingsLink = false;
		this.activeReviewsLink = false;
		this.activeSocialPostsLinks = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeReportsRouterLink = false;
		this.activeAiVisibilityLink = false;
		this.activeDataChatRouterLink = true;
	}

	processReportsRoutes() {
		this.activePortfolioRouterLink = false;
		this.activeAdminRouterLink = false;
		this.activeClientSettingsRouterLink = false;
		this.activeKPIManagementRouterLink = false;
		this.activeBudgetTrackerRouterLink = false;
		this.activeAutomationRouterLink = false;
		this.activeInsightsRouterLink = false;
		this.activeDataWareHouseRouterLink = false;
		this.activeExtensionLink = false;
		this.activeAnomalyDetectionRouterLink = false;
		this.activeAnalysisLink = false;
		this.activeLocalListingsLink = false;
		this.activeReviewsLink = false;
		this.activeSocialPostsLinks = false;
		this.activeGetStartedLink = false;
		this.activeDataSourcesRouterLink = false;
		this.activeDataChatRouterLink = false;
		this.activeReportsRouterLink = true;
		this.activeAiVisibilityLink = false;
	}

	left(): void {
		this.isCollapse = true;
		this.applySidebarWidths(true);
		this.jepGlobalDataService.setMainMenuCollapsed(true);
	}

	right(): void {
		this.isCollapse = false;
		this.applySidebarWidths(false);
		this.jepGlobalDataService.setMainMenuCollapsed(false);
	}

	openLatestUpdates(): void {
		if (!window.FrillWidget) {
			return;
		}
		window.FrillWidget.viewSection('announcements');
		window.FrillWidget.open();
	}

	onNavScroll(): void {
		this.syncNavOverflowFade();
	}

	private applySidebarWidths(collapsed: boolean): void {
		document.documentElement.style.setProperty(
			'--main-sidebar-current-width',
			mainSidebarCurrentWidthValue(collapsed)
		);
		const sidebar = document.getElementById('jep-sidebar');
		const container = document.getElementById('jep-container');
		const topbar = document.getElementById('jep-topbar');
		if (!sidebar || !container || !topbar) {
			return;
		}
		if (collapsed) {
			sidebar.style.width = '82px';
			container.style.gridTemplateColumns = '82px 1fr';
			topbar.style.gridTemplateColumns = '82px 1fr';
			requestAnimationFrame(() => this.syncNavOverflowFade());
			return;
		}
		sidebar.style.width = '255px';
		container.style.gridTemplateColumns = '255px 1fr';
		topbar.style.gridTemplateColumns = '255px 1fr';
		requestAnimationFrame(() => this.syncNavOverflowFade());
	}

	private bindNavOverflowFade(): void {
		const el = this.navMenu?.nativeElement;
		if (!el || !NAV_OVERFLOW_FADE_ENABLED) {
			return;
		}
		this.navOverflowFadeObserver = new ResizeObserver(() => {
			this.syncNavOverflowFade();
		});
		this.navOverflowFadeObserver.observe(el);
		this.syncNavOverflowFade();
	}

	private syncNavOverflowFade(): void {
		const el = this.navMenu?.nativeElement;
		if (!el) {
			return;
		}
		const next = navOverflowFadeState(
			el.scrollTop,
			el.clientHeight,
			el.scrollHeight
		);
		if (
			this.navMoreAbove === next.moreAbove &&
			this.navMoreBelow === next.moreBelow
		) {
			return;
		}
		safeUpdateInSubscription(this.cdr, () => {
			this.navMoreAbove = next.moreAbove;
			this.navMoreBelow = next.moreBelow;
		});
	}
}
